// =====================================================================================
// FILE: src/components/table/CategorySection.jsx
// =====================================================================================

import React from "react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { THEME, FONTS } from "../../constants/theme";
import HeaderRow from "./HeaderRow";
import SortableRow from "./SortableRow";

// ✅ Contextual Bulk Action Bar Component
function BulkActionBar({
  selectedCount,
  allCategories,
  bulkCopyTargetCategories,
  onBulkCopyTargetChange,
  onBulkCopy,
  onBulkMove,
  onBulkDelete,
  onClearSelection,
  saving,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const toggleCategory = (cat) => {
    const current = bulkCopyTargetCategories || [];
    if (current.includes(cat)) {
      onBulkCopyTargetChange(current.filter(c => c !== cat));
    } else {
      onBulkCopyTargetChange([...current, cat]);
    }
  };

  return (
    <div style={{
      background: THEME.dark,
      padding: "10px 14px",
      borderRadius: THEME.radiusMd,
      marginBottom: 12,
      display: "flex",
      alignItems: "center",
      gap: 10,
      boxShadow: THEME.shadowLg,
      fontFamily: FONTS.family,
    }}>
      <span style={{ 
        fontWeight: FONTS.semibold, 
        color: THEME.textOnDark, 
        fontSize: FONTS.xs,
        padding: "5px 10px",
        background: THEME.primary,
        borderRadius: THEME.radius,
        whiteSpace: "nowrap",
      }}>
        {selectedCount} row{selectedCount !== 1 ? 's' : ''}
      </span>
      
      {/* Multi-Select Category Dropdown - COMPACT */}
      <div ref={dropdownRef} style={{ position: "relative", minWidth: 140, maxWidth: 160 }}>
        <div
          onClick={() => !saving && setIsDropdownOpen(!isDropdownOpen)}
          style={{
            padding: "6px 10px",
            borderRadius: THEME.radius,
            border: "none",
            background: THEME.surface,
            fontWeight: FONTS.medium,
            fontSize: FONTS.xs,
            fontFamily: FONTS.family,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1,
            minHeight: 28,
            display: "flex",
            alignItems: "center",
            gap: 4,
            flexWrap: "nowrap",
            overflow: "hidden",
          }}
        >
          {(bulkCopyTargetCategories || []).length === 0 ? (
            <span style={{ opacity: 0.5, color: THEME.textSecondary, fontSize: FONTS.xs, whiteSpace: "nowrap" }}>Select categories...</span>
          ) : (
            <span style={{
              fontSize: FONTS.xs,
              fontWeight: FONTS.medium,
              color: THEME.text,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
              {(bulkCopyTargetCategories || []).length} selected
            </span>
          )}
        </div>
        
        {isDropdownOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              marginTop: 4,
              background: THEME.surface,
              borderRadius: THEME.radius,
              boxShadow: THEME.shadowLg,
              maxHeight: 180,
              overflowY: "auto",
              zIndex: 1001,
              border: `1px solid ${THEME.line}`,
            }}
          >
            {allCategories.map(cat => {
              const isSelected = (bulkCopyTargetCategories || []).includes(cat);
              return (
                <label
                  key={cat}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 10px",
                    cursor: saving ? "not-allowed" : "pointer",
                    background: isSelected ? THEME.primaryLight : "transparent",
                    color: THEME.text,
                    fontWeight: FONTS.medium,
                    fontSize: FONTS.xs,
                    fontFamily: FONTS.family,
                    transition: THEME.transitionFast,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleCategory(cat)}
                    disabled={saving}
                    style={{ accentColor: THEME.primary }}
                  />
                  {cat}
                </label>
              );
            })}
          </div>
        )}
      </div>
      
      <button
        onClick={onBulkCopy}
        disabled={(bulkCopyTargetCategories || []).length === 0 || saving}
        style={{
          padding: "6px 12px",
          borderRadius: THEME.radius,
          fontWeight: FONTS.semibold,
          fontSize: FONTS.xs,
          fontFamily: FONTS.family,
          background: THEME.surface,
          color: THEME.text,
          border: "none",
          cursor: ((bulkCopyTargetCategories || []).length === 0 || saving) ? "not-allowed" : "pointer",
          opacity: ((bulkCopyTargetCategories || []).length === 0 || saving) ? 0.5 : 1,
          whiteSpace: "nowrap",
          transition: THEME.transitionFast,
        }}
      >
        {saving ? "..." : "Copy"}
      </button>
      
      <button
        onClick={onBulkMove}
        disabled={(bulkCopyTargetCategories || []).length === 0 || saving}
        style={{
          padding: "6px 12px",
          borderRadius: THEME.radius,
          fontWeight: FONTS.semibold,
          fontSize: FONTS.xs,
          fontFamily: FONTS.family,
          background: THEME.info,
          color: THEME.textOnDark,
          border: "none",
          cursor: ((bulkCopyTargetCategories || []).length === 0 || saving) ? "not-allowed" : "pointer",
          opacity: ((bulkCopyTargetCategories || []).length === 0 || saving) ? 0.5 : 1,
          whiteSpace: "nowrap",
          transition: THEME.transitionFast,
        }}
      >
        {saving ? "..." : "Move"}
      </button>
      
      <button
        onClick={onBulkDelete}
        disabled={saving}
        style={{
          padding: "6px 12px",
          borderRadius: THEME.radius,
          fontWeight: FONTS.semibold,
          fontSize: FONTS.xs,
          fontFamily: FONTS.family,
          background: THEME.error,
          color: THEME.textOnDark,
          border: "none",
          cursor: saving ? "not-allowed" : "pointer",
          opacity: saving ? 0.5 : 1,
          whiteSpace: "nowrap",
          transition: THEME.transitionFast,
        }}
      >
        {saving ? "..." : "Delete"}
      </button>
      
      <button
        onClick={onClearSelection}
        disabled={saving}
        style={{
          padding: "6px 12px",
          borderRadius: THEME.radius,
          fontWeight: FONTS.semibold,
          fontSize: FONTS.xs,
          fontFamily: FONTS.family,
          background: "transparent",
          border: `1px solid rgba(255,255,255,0.3)`,
          color: THEME.textOnDark,
          cursor: saving ? "not-allowed" : "pointer",
          opacity: saving ? 0.5 : 1,
          whiteSpace: "nowrap",
          transition: THEME.transitionFast,
        }}
      >
        Cancel
      </button>
    </div>
  );
}

export default function CategorySection({
  category,
  items,
  visibleCols,
  stickyLeftMap,
  template,
  minWidth,
  assetCache,
  isBusy,
  editingCell,
  setEditingCell,
  onCopy,
  onMove,
  onDelete,
  onInlineSave,
  visibleKeys,
  isIptvMode,
  onCreateEmptyRow,
  onCancelLastCreated,
  lastInlineCreatedId,
  saving,
  isLedMode,
  onShuffleLed,
  isRowLocked,
  iptvOptions,
  selectedRowIds,
  onToggleRowSelection,
  onClearSelection,
  bulkCopyTargetCategories,
  onBulkCopyTargetChange,
  onBulkCopy,
  onBulkMove,
  onBulkDelete,
  allCategories,
  showActionBar,
  searchTerm,
  readOnly = false, // ✅ Read-only mode for venue_ops
}) {
  const hasOpenAssetPicker = items.some(item => 
    editingCell?.id === item.id && [
      "bigGfxVideos", "cornerGfxVideos", "ledAllTypes", "bigTvc", "cornerTvc", "mcgTiledLed", "marvelTiledLed", 
      "superFull", "super169", "superSide", "mcgInterchange", "marvelInterchange", 
      "marvel360", "mcg360", "iptvGfx"
    ].includes(editingCell?.col)
  );

  const showShuffleButton = isLedMode && category === "Allocations" && onShuffleLed;
  const selectedInThisCategory = items.filter(item => selectedRowIds && selectedRowIds.has(item.id)).length;
  
  // ✅ Only show cancel button if the lastInlineCreatedId belongs to THIS category's items
  const showCancelButton = lastInlineCreatedId && items.some(item => item.id === lastInlineCreatedId);

  // ✅ Collapsible state
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  return (
    <div style={{ 
      marginTop: 24, 
      marginBottom: 32, 
      minWidth,
      overflow: hasOpenAssetPicker ? "visible" : "hidden",
      fontFamily: FONTS.family,
    }}>
      {/* Bulk Action Bar */}
      {showActionBar && selectedInThisCategory > 0 && (
        <BulkActionBar
          selectedCount={selectedRowIds.size}
          allCategories={allCategories}
          bulkCopyTargetCategories={bulkCopyTargetCategories}
          onBulkCopyTargetChange={onBulkCopyTargetChange}
          onBulkCopy={onBulkCopy}
          onBulkMove={onBulkMove}
          onBulkDelete={onBulkDelete}
          onClearSelection={onClearSelection}
          saving={saving}
        />
      )}

      {/* ✅ Category Header - Now clickable to collapse */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          marginBottom: isCollapsed ? 0 : 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Category Label - Clickable to toggle collapse */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{
              fontWeight: FONTS.semibold,
              fontSize: FONTS.sm,
              color: THEME.textOnDark,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 16px",
              borderRadius: THEME.radiusMd,
              background: THEME.dark,
              boxShadow: THEME.shadowMd,
              border: "none",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {/* Collapse Arrow */}
            <span style={{
              display: "inline-block",
              transition: "transform 0.2s ease",
              transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
              fontSize: 12,
            }}>
              ▼
            </span>
            {category}
            {/* Item count badge */}
            <span style={{
              background: "rgba(255,255,255,0.2)",
              padding: "2px 8px",
              borderRadius: 10,
              fontSize: FONTS.xs,
              fontWeight: FONTS.medium,
            }}>
              {items.length}
            </span>
            {/* Read-only badge for venue ops */}
            {readOnly && (
              <span style={{
                background: "rgba(251, 146, 60, 0.3)",
                color: "#fb923c",
                padding: "2px 8px",
                borderRadius: 10,
                fontSize: FONTS.xs,
                fontWeight: FONTS.semibold,
                marginLeft: 4,
              }}>
                👁️ View Only
              </span>
            )}
          </button>

          {/* ✅ New Content Row Button - Hidden when collapsed or readOnly */}
          {!isCollapsed && !readOnly && onCreateEmptyRow && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCreateEmptyRow(category);
              }}
              disabled={saving}
              className="add-content-row-btn"
              style={{
                border: "none",
                background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
                color: "#000",
                borderRadius: THEME.radiusMd,
                padding: "10px 16px",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: FONTS.semibold,
                fontSize: FONTS.sm,
                fontFamily: FONTS.family,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                opacity: saving ? 0.6 : 1,
                boxShadow: THEME.shadowMd,
                transition: "all 0.15s ease",
              }}
            >
              <span style={{ fontSize: 16, lineHeight: 1, fontWeight: FONTS.bold }}>+</span>
              New Content Row
            </button>
          )}

          {/* ✅ Cancel button - Hidden when collapsed */}
          {!isCollapsed && showCancelButton && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCancelLastCreated();
              }}
              disabled={saving}
              className="cancel-row-btn"
              style={{
                border: `1px solid ${THEME.line}`,
                background: THEME.surface,
                color: THEME.textSecondary,
                borderRadius: THEME.radiusMd,
                padding: "10px 14px",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: FONTS.medium,
                fontSize: FONTS.sm,
                fontFamily: FONTS.family,
                opacity: saving ? 0.4 : 1,
                transition: "all 0.15s ease",
                boxShadow: THEME.shadowSm,
              }}
            >
              Cancel
            </button>
          )}

          {!isCollapsed && showShuffleButton && (
            <button
              type="button"
              onClick={onShuffleLed}
              disabled={saving}
              style={{
                border: "none",
                background: THEME.secondary,
                color: THEME.dark,
                borderRadius: THEME.radiusMd,
                padding: "10px 14px",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: FONTS.semibold,
                fontSize: FONTS.sm,
                fontFamily: FONTS.family,
                boxShadow: THEME.shadowSm,
                opacity: saving ? 0.6 : 1,
                transition: THEME.transitionFast,
              }}
              title="Shuffle LED sequence: Toyota first, Telstra second, then others"
            >
              🔀 Shuffle Sequence
            </button>
          )}
        </div>
      </div>

      {/* ✅ Collapsible Content */}
      {!isCollapsed && (
        <>
          {/* Table Header */}
          <HeaderRow cols={visibleCols} stickyLeftMap={stickyLeftMap} template={template} />

          {/* Table Rows */}
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            {items.map((it) => (
              <SortableRow
                key={it.id}
                item={it}
                assetCache={assetCache}
                isBusy={isBusy}
                editingCell={editingCell}
                setEditingCell={setEditingCell}
                onCopy={onCopy}
                onMove={onMove}
                onDelete={onDelete}
                onInlineSave={onInlineSave}
                visibleKeys={visibleKeys}
                stickyLeftMap={stickyLeftMap}
                template={template}
                isIptvMode={isIptvMode}
                isRowLocked={isRowLocked}
                iptvOptions={iptvOptions}
                isSelected={selectedRowIds && selectedRowIds.has(it.id)}
                onToggleSelect={onToggleRowSelection}
                searchTerm={searchTerm}
                categories={allCategories}
              />
            ))}
          </SortableContext>

      {/* ✅ Quick Add Row - Bottom dashed button */}
      <div style={{ 
        marginTop: 16,
        display: "flex",
        justifyContent: "center",
      }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCreateEmptyRow(category);
          }}
          disabled={saving}
          className="quick-add-row-btn"
          style={{
            border: `2px dashed ${THEME.line}`,
            background: "transparent",
            color: THEME.textSecondary,
            borderRadius: THEME.radiusMd,
            padding: "14px 28px",
            cursor: saving ? "not-allowed" : "pointer",
            fontWeight: FONTS.medium,
            fontSize: FONTS.sm,
            fontFamily: FONTS.family,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            opacity: saving ? 0.3 : 0.5,
            transition: "all 0.15s ease",
          }}
          title={`Quick add to ${category}`}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
          Quick Add Row
        </button>
      </div>
        </>
      )}
    </div>
  );
}
